export default defineNuxtPlugin(async (nuxtApp) => {
  const { user, session } = useSession()
  const axios = useAxios()

  const state = reactive({
    socket: null as WebSocket | null,
    notifications: [] as any[],
    reconnectAttempts: 0,
    reconnectTimeout: null as ReturnType<typeof setTimeout> | null,
  })

  // Fetch initial notifications from the API
  const fetchInitialNotifications = async () => {
    try {
      const response = await axios.get('/internal/notifications')
      state.notifications = response.data
    } catch (err) {
      console.error('Error fetching initial notifications:', err)
    }
  }

   // Clean up the WebSocket and related states
  const cleanupSocket = () => {
    if (state.socket) {
      state.socket.close()
      state.socket = null
      state.notifications = []
    }

    if (state.reconnectTimeout) {
      clearTimeout(state.reconnectTimeout)
      state.reconnectTimeout = null
    }

    state.reconnectAttempts = 0
  }

  // Schedule a WebSocket reconnection attempt
  const reconnect = () => {
    state.reconnectAttempts++
    const delay = Math.min(1000 * 2 ** state.reconnectAttempts, 10000)
    state.reconnectTimeout = setTimeout(() => {
      setupWebSocket()
    }, delay)
  }

  // Create and manage the WebSocket connection
  const setupWebSocket = () => {
    if (!user.value) return

    const socket = new WebSocket(`${useRuntimeConfig().public.websocketURL}?token=${session.value.token}`)
    state.socket = socket

    socket.onopen = () => {
      state.reconnectAttempts = 0
      fetchInitialNotifications()
    }

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        state.notifications.push(data)
      } catch (e) {
        console.error('Error parsing message:', e)
      }
    }

    socket.onclose = () => {
      state.socket = null
      reconnect()
    }

    socket.onerror = (error) => {
      console.error('WebSocket error:', error)
      socket.close()
    }
  }

  // Watch for user changes to reinitialize the WebSocket when necessary
  watch(user, (newUser, oldUser) => {
    if (!newUser || !oldUser || newUser.id !== oldUser.id) {
      cleanupSocket()
      setupWebSocket()
    }
  }, { immediate: true })

  nuxtApp.provide('webSocket', state)
})
